'use client'

/**
 * U8 — Payment Element checkout step (R12, R14, R24, R29, R33; KTD3, KTD4).
 *
 * Renders AFTER a paid order is created (`createOrder` returns `clientSecret`
 * + `stripeAccountId` for a paid order held `awaiting_payment`, U6). Stripe.js
 * is loaded in the CONNECTED-account context (KTD3 — direct charges,
 * `stripeAccount: stripeAccountId`), never the platform account. Card data
 * never reaches our servers — the Payment Element keeps entry inside
 * Stripe-hosted iframes (R33, SAQ-A posture).
 *
 * The order is marked paid ONLY by the Stripe webhook (KTD4 — server-
 * authoritative). `confirmPayment`'s return value here is display/navigation
 * plumbing only: it tells the browser where to go next, never whether the
 * order is "really" paid — the confirmation page's status poll is the
 * source of truth for that.
 *
 * Interaction states (plan U8, mapped 1:1 to code):
 *   (a) checkout loading   — the RSVP form's existing "Submitting…" state
 *       covers order+PaymentIntent creation; `!elementsReady` below covers
 *       the remaining wait for the Payment Element iframe to mount.
 *   (b) submitting          — `submitting`, disables the button + spinner
 *       label for the duration of `confirmPayment` (including a possible
 *       3DS redirect, which navigates the page away entirely).
 *   (c) pending→paid        — NOT this component; the confirmation page polls
 *       (see confirmation/payment-status-panel.tsx).
 *   (d) decline/SCA-fail/network error — `errorMessage` + `seatReleased`.
 *   (e) seat-hold countdown — `msRemaining` / `<HoldCountdown>`.
 */

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { StripePaymentElementOptions, PaymentIntent } from '@stripe/stripe-js'
import { type FormEvent, useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getStripeClient, isStripeCheckoutConfigured } from '@/lib/stripe-client'
import { PAID_HOLD_TIMEOUT_MS, fetchPublicOrderStatus, formatCountdown } from './_lib/order-status'

export interface CheckoutSucceededInfo {
  paymentIntentClientSecret: string
  status: PaymentIntent.Status
}

export interface CheckoutPaymentStepProps {
  schoolSlug: string
  referenceCode: string
  buyerEmail: string
  clientSecret: string
  stripeAccountId: string
  /** `Date.now()` captured when the browser received the order-creation
   * response — the anchor for the client-side hold countdown (see
   * `_lib/order-status.ts`). */
  createdAtMs: number
  /** Absolute URL Stripe redirects to ONLY if a payment method truly requires
   * leaving the page (e.g. some bank 3DS flows) — `redirect: 'if_required'`
   * below keeps everything else in-page. */
  returnUrl: string
  onSucceeded: (info: CheckoutSucceededInfo) => void
  onRestart: () => void
}

export function CheckoutPaymentStep(props: CheckoutPaymentStepProps) {
  const stripePromise = getStripeClient(props.stripeAccountId)

  if (!isStripeCheckoutConfigured() || !stripePromise) {
    // Should not happen for a real paid order in a configured environment
    // (checkout only reaches this step once the backend already minted a
    // clientSecret) — surfaced as an actionable message rather than a silent
    // hang, mirroring the backend's optional-secret posture.
    return (
      <Alert variant="destructive">
        <AlertTitle>Payment is not available right now</AlertTitle>
        <AlertDescription>Please contact the school directly to complete this registration.</AlertDescription>
      </Alert>
    )
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: props.clientSecret, appearance: { theme: 'stripe' } }}>
      <PaymentStepInner {...props} />
    </Elements>
  )
}

function PaymentStepInner({
  schoolSlug,
  referenceCode,
  buyerEmail,
  createdAtMs,
  returnUrl,
  onSucceeded,
  onRestart
}: CheckoutPaymentStepProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [elementsReady, setElementsReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [seatReleased, setSeatReleased] = useState(false)
  const [msRemaining, setMsRemaining] = useState(() => PAID_HOLD_TIMEOUT_MS - (Date.now() - createdAtMs))

  const expired = msRemaining <= 0

  useEffect(() => {
    const id = setInterval(() => {
      setMsRemaining(PAID_HOLD_TIMEOUT_MS - (Date.now() - createdAtMs))
    }, 1000)
    return () => clearInterval(id)
  }, [createdAtMs])

  // (d) Before offering retry, re-check seat availability (R14) — a hold that
  // already expired/released must not dead-end into a retry against a seat
  // that's gone.
  async function recheckSeatAfterError() {
    setCheckingAvailability(true)
    const snapshot = await fetchPublicOrderStatus({ schoolSlug, referenceCode, buyerEmail })
    setCheckingAvailability(false)
    if (snapshot && snapshot.status !== 'awaiting_payment') {
      setSeatReleased(true)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements || submitting || expired) return
    setErrorMessage(null)
    setSeatReleased(false)
    setSubmitting(true)
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required'
      })

      if (error) {
        setErrorMessage(declineMessage(error))
        await recheckSeatAfterError()
        return
      }

      // Any other status with no error (e.g. a real redirect already under
      // way) leaves the page via the browser navigation Stripe performs.
      if (isInlineConfirmation(paymentIntent)) {
        onSucceeded({ paymentIntentClientSecret: paymentIntent.client_secret, status: paymentIntent.status })
      }
    } catch {
      setErrorMessage('A network error interrupted your payment. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const paymentElementOptions: StripePaymentElementOptions = { layout: 'tabs' }
  const blockedByHold = expired || seatReleased

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit} data-testid="checkout-payment-step">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Payment</h2>
        <HoldCountdown msRemaining={msRemaining} />
      </div>

      {!elementsReady && (
        <div className="flex flex-col gap-3" aria-hidden="true" data-testid="payment-loading-skeleton">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      )}

      <div className={elementsReady ? undefined : 'pointer-events-none absolute -z-10 h-0 overflow-hidden opacity-0'}>
        <PaymentElement options={paymentElementOptions} onReady={() => setElementsReady(true)} />
      </div>

      <HoldExpiredAlert expired={expired} />
      <PaymentErrorAlert expired={expired} errorMessage={errorMessage} seatReleased={seatReleased} />

      <PaymentActionButton
        blockedByHold={blockedByHold}
        onRestart={onRestart}
        canSubmit={Boolean(stripe) && elementsReady}
        submitting={submitting}
        checkingAvailability={checkingAvailability}
      />
    </form>
  )
}

// (e) Hold timed out before payment completed — restart is the only path.
function HoldExpiredAlert({ expired }: { expired: boolean }) {
  if (!expired) return null
  return (
    <Alert variant="destructive">
      <AlertTitle>Your hold expired</AlertTitle>
      <AlertDescription>
        Your reserved spot timed out before payment completed. Please restart to try again.
      </AlertDescription>
    </Alert>
  )
}

// (d) Decline / SCA-fail / network error. A hold released mid-attempt swaps the
// copy from a retriable decline to a "seat gone" message.
function PaymentErrorAlert({
  expired,
  errorMessage,
  seatReleased
}: {
  expired: boolean
  errorMessage: string | null
  seatReleased: boolean
}) {
  if (expired || !errorMessage) return null
  return (
    <Alert variant="destructive">
      <AlertTitle>{seatReleased ? 'This seat is no longer available' : 'Payment did not go through'}</AlertTitle>
      <AlertDescription>
        {seatReleased
          ? 'Your hold expired while we processed that attempt and the seat was released. Please restart to check for availability.'
          : errorMessage}
      </AlertDescription>
    </Alert>
  )
}

// Submit when the hold is live; once expired/released the only action is Restart.
function PaymentActionButton({
  blockedByHold,
  onRestart,
  canSubmit,
  submitting,
  checkingAvailability
}: {
  blockedByHold: boolean
  onRestart: () => void
  canSubmit: boolean
  submitting: boolean
  checkingAvailability: boolean
}) {
  if (blockedByHold) {
    return (
      <Button type="button" size="lg" onClick={onRestart}>
        Restart
      </Button>
    )
  }
  return (
    <Button type="submit" size="lg" disabled={!canSubmit || submitting || checkingAvailability}>
      {submitting ? 'Processing…' : checkingAvailability ? 'Checking availability…' : 'Pay now'}
    </Button>
  )
}

/** True when `confirmPayment` returned an in-page terminal success (no redirect
 * pending) that the browser should navigate on — narrows `client_secret` to a
 * string for the caller. */
function isInlineConfirmation(pi: PaymentIntent | null | undefined): pi is PaymentIntent & { client_secret: string } {
  return Boolean(pi?.client_secret) && (pi?.status === 'succeeded' || pi?.status === 'processing')
}

function HoldCountdown({ msRemaining }: { msRemaining: number }) {
  const urgent = msRemaining <= 60_000 && msRemaining > 0
  return (
    <span
      className={urgent ? 'text-sm font-medium text-destructive' : 'text-sm text-muted-foreground'}
      aria-live="polite"
      data-testid="hold-countdown"
    >
      {msRemaining > 0 ? `Complete within ${formatCountdown(msRemaining)}` : 'Hold expired'}
    </span>
  )
}

/** Distinct inline message sourced from Stripe's error/decline_code (d). */
function declineMessage(error: { type?: string; code?: string; decline_code?: string; message?: string }): string {
  if (error.decline_code) {
    const known: Record<string, string> = {
      insufficient_funds: 'Your card has insufficient funds. Please try a different payment method.',
      lost_card: 'This card cannot be used. Please try a different payment method.',
      stolen_card: 'This card cannot be used. Please try a different payment method.',
      expired_card: 'Your card has expired. Please try a different payment method.',
      incorrect_cvc: 'The security code was incorrect. Please check it and try again.',
      processing_error: 'A processing error occurred on your bank’s side. Please try again.',
      generic_decline: 'Your card was declined. Please try a different payment method.'
    }
    return (
      known[error.decline_code] ??
      `Your card was declined (${error.decline_code}). Please try a different payment method.`
    )
  }
  if (error.type === 'validation_error') {
    return error.message ?? 'Please check your payment details and try again.'
  }
  return error.message ?? 'Something went wrong confirming your payment. Please try again.'
}

// Exported for direct unit testing without mounting Stripe Elements.
export { declineMessage }
